import { ref, computed, Ref, watch, shallowRef } from 'vue';
import {
  getVendingMachineByCoordinates,
  getVendingMachines,
  VendingMachine
} from '@/api/requests/VendingMachines';
import { useLocation } from './useLocation';
import { LngLat } from '@yandex/ymaps3-types';

const allVendingMachines = ref<VendingMachine[] | null>(null);
const allVendingMachinesPromise = shallowRef<Promise<VendingMachine[]> | null>(null);

const vendingMachinesForUserLocation = ref<VendingMachine[] | null>(null);
const vendingMachinesForUserLocationPromise = shallowRef<Promise<VendingMachine[] | null> | null>(
  null
);
const vendingMachinesForUserLocationError = ref<Error | null>(null);

const isLocationLoading = ref(false);
const userLocation = ref<LngLat | null>(null);

/**
 * Функция для расчёта расстояния между двумя координатами на Земле.
 * @param {number} userLat - широта точки (пользователь)
 * @param {number} userLng - долгота точки (пользователь)
 * @param {number} placeLat - широта другой точки (место)
 * @param {number} placeLng - долгота другой точки (место)
 * @returns {number} расстояние в километрах
 */
function getDistanceInKm(userLat: number, userLng: number, placeLat: number, placeLng: number) {
  // Радиус Земли в километрах
  const R = 6371;

  // Переводим градусы в радианы
  const dLat = ((placeLat - userLat) * Math.PI) / 180;
  const dLng = ((placeLng - userLng) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((userLat * Math.PI) / 180) *
      Math.cos((placeLat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  // Итоговое расстояние в километрах
  const distance = R * c;
  return distance;
}

async function loadVendingMachines() {
  if (allVendingMachinesPromise.value) {
    return allVendingMachinesPromise.value;
  }

  async function createPromise() {
    allVendingMachines.value = await getVendingMachines();

    return allVendingMachines.value;
  }

  allVendingMachinesPromise.value = createPromise();

  return allVendingMachines.value;
}

function waitForVendingMachines() {
  if (!allVendingMachinesPromise.value) {
    return loadVendingMachines();
  }

  return allVendingMachinesPromise.value;
}

export function useVendingMachinesList() {
  const { getLocation } = useLocation();

  const filterByLocationCheckbox = ref(false);

  const isLocationError = computed(() => !userLocation.value && !isLocationLoading.value);

  const isFilteringByLocationLoading = computed(
    () =>
      !!vendingMachinesForUserLocationPromise.value &&
      !vendingMachinesForUserLocation.value &&
      !vendingMachinesForUserLocationError.value
  );

  const isLoaded = computed(() => allVendingMachines.value !== null);

  async function loadVendingMachinesForUserLocation() {
    if (!userLocation.value) {
      return null;
    }

    async function createPromise() {
      if (!userLocation.value) {
        return null;
      }

      try {
        vendingMachinesForUserLocation.value = await getVendingMachineByCoordinates(
          userLocation.value[0],
          userLocation.value[1]
        );
      } catch (error) {
        vendingMachinesForUserLocation.value = null;
        vendingMachinesForUserLocationError.value = error as Error;
        filterByLocationCheckbox.value = false;
        // eslint-disable-next-line no-console
        console.error(error);
      }

      return vendingMachinesForUserLocation.value;
    }

    vendingMachinesForUserLocationPromise.value = createPromise();

    return vendingMachinesForUserLocation.value;
  }

  function createSortMachinesComputed() {
    async function initUserLocation() {
      isLocationLoading.value = true;
      userLocation.value = await getLocation();
      isLocationLoading.value = false;
    }

    const sortedMachines = computed(() => {
      if (!userLocation.value) {
        // Если местоположение ещё не получено, возвращаем исходный порядок
        return allVendingMachines.value;
      }

      let neededMachines: VendingMachine[] = [];
      if (filterByLocationCheckbox?.value && vendingMachinesForUserLocation.value) {
        neededMachines = vendingMachinesForUserLocation.value;
      } else {
        neededMachines = allVendingMachines.value ?? [];
      }

      return [...neededMachines].sort((a, b) => {
        const coordinatesA = a.attributes.Coordinates?.coordinates;
        const coordinatesB = b.attributes.Coordinates?.coordinates;

        // Проверяем, есть ли координаты (lat и lng) у каждой машины
        const hasCoordsA = !!(coordinatesA && coordinatesA.lat && coordinatesA.lng);
        const hasCoordsB = !!(coordinatesB && coordinatesB.lat && coordinatesB.lng);

        // 1. Если A не имеет координат, а B имеет ⇒ A в конце
        if (!hasCoordsA && hasCoordsB) {
          return 1; // A > B
        }
        // 2. Если B не имеет координат, а A имеет ⇒ B в конце
        if (hasCoordsA && !hasCoordsB) {
          return -1; // A < B
        }
        // 3. Если оба не имеют координат ⇒ равны (или любая другая логика)
        if ((!hasCoordsA && !hasCoordsB) || !userLocation.value) {
          return 0;
        }

        // 4. Если у обоих есть координаты, сортируем по расстоянию
        const [lng, lat] = userLocation.value;
        const distanceA = getDistanceInKm(lat, lng, coordinatesA.lat, coordinatesA.lng);
        const distanceB = getDistanceInKm(lat, lng, coordinatesB.lat, coordinatesB.lng);

        return distanceA - distanceB;
      });
    });

    if (filterByLocationCheckbox) {
      watch(filterByLocationCheckbox, async () => {
        if (!vendingMachinesForUserLocation.value && !vendingMachinesForUserLocationPromise.value) {
          loadVendingMachinesForUserLocation();
        }
      });
    }

    return {
      initUserLocation,
      sortedMachines,
      isLocationError
    };
  }

  return {
    allVendingMachines,
    filterByLocationCheckbox,
    createSortMachinesComputed,
    loadVendingMachines,
    waitForVendingMachines,
    isLocationError,
    isLocationLoading,
    userLocation,
    isFilteringByLocationLoading,
    isLoaded,
    vendingMachinesForUserLocationError
  };
}
